import { useCallback, useEffect, useRef } from 'react';
import {
  SCORM_MESSAGE_KINDS,
  type ScormPlayerToShim,
  type ScormResumeResponseMessage,
} from './scormMessages';
import {
  coverageSeconds,
  mergeSegments,
  parseSuspendData,
  serializeSuspendData,
  type WatchedSegment,
} from './watchProgress';

type VideoJsLikePlayer = {
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
  currentTime: () => number;
  duration: () => number;
  // video.js exposes this; once true, calling currentTime()/duration() throws
  // because the player's internals have been nulled out on dispose.
  isDisposed?: () => boolean;
  seeking?: () => boolean;
};

type PlayerWithSeek = { currentTime: (seconds: number) => void };

type UseScormTrackingOptions = {
  enabled: boolean;
  completionThreshold?: number;
  /**
   * Block forward seeks past the furthest naturally-watched position.
   * The lock lifts automatically once the lesson is completed.
   */
  disableSeek?: boolean;
  /**
   * Fires once when the lesson becomes (or is discovered to already be)
   * completed — the moment all seek restrictions stop applying. The host can
   * use it to restore the full player UI.
   */
  onCompleted?: () => void;
};

type SetupPlayerTracking = (player: VideoJsLikePlayer | null) => void;

const SET_LOCATION_INTERVAL_MS = 5000;

/**
 * timeupdate fires ~4×/s, so consecutive ticks during continuous playback are
 * fractions of a second apart (≲0.5s even at 2× speed). A larger forward jump
 * is a seek — it must never be credited as watched.
 */
const NATURAL_DELTA_MAX_SECONDS = 1.5;

/** Slack for the forward-seek lock; video.js can overshoot by a frame or two. */
const FORWARD_SEEK_TOLERANCE_SECONDS = 0.5;

/**
 * Browsers stop firing timeupdate a fraction of a second before `duration`,
 * so watched coverage realistically caps just short of it. Treat "all but the
 * last 1.5s" as full so a 100% threshold is actually reachable.
 */
const END_TOLERANCE_SECONDS = 1.5;

/**
 * Best-effort discovery of which origin(s) host this iframe so the SCORM
 * postMessage protocol can use a tight `targetOrigin` (instead of '*') and
 * reject spoofed inbound messages.
 *
 * Two fallback layers:
 *   1. `window.location.ancestorOrigins` — Chromium/WebKit only. Firefox does
 *      not expose this API at all, so the property is `undefined` there.
 *   2. `document.referrer` — works most of the time, but is empty when the LMS
 *      sets `Referrer-Policy: no-referrer` (or strict variants over HTTP→HTTPS
 *      hops), or when the iframe is sandboxed without `allow-same-origin`.
 *
 * If BOTH fail (the only realistic case being Firefox + a referrer-stripping
 * LMS), this returns []. Downstream:
 *   - outbound `postMessage` falls back to `targetOrigin: '*'` — leaks SCORM
 *     progress to any sibling iframe in the same LMS page.
 *   - inbound message handler accepts any origin — a malicious sibling could
 *     spoof `SCORM_COMPLETE`.
 *
 * TODO: harden this by having the in-ZIP shim (`buildScormShim`) inject its
 * own `window.location.origin` into the iframe `src` as a `scormShimOrigin`
 * query param BEFORE the iframe loads. The hook would then prefer that param
 * over discovery, making parent-origin resolution deterministic on every
 * browser regardless of referrer policy.
 */
function discoverParentOrigins(): string[] {
  const origins = new Set<string>();
  try {
    const ancestors = (
      globalThis.location as Location & { ancestorOrigins?: DOMStringList }
    ).ancestorOrigins;
    if (ancestors) {
      for (let index = 0; index < ancestors.length; index += 1) {
        const origin = ancestors[index];
        if (origin && origin !== 'null') origins.add(origin);
      }
    }
  } catch {
    // Firefox throws on ancestorOrigins access in some configs.
  }

  if (document.referrer) {
    try {
      origins.add(new URL(document.referrer).origin);
    } catch {
      // Ignore invalid referrer values.
    }
  }
  return [...origins];
}

export function useScormTracking({
  enabled,
  completionThreshold = 1,
  disableSeek = false,
  onCompleted,
}: UseScormTrackingOptions): { setupPlayerTracking: SetupPlayerTracking } {
  const parentOriginsRef = useRef<string[]>([]);
  const initSentRef = useRef(false);
  const completedRef = useRef(false);
  const pendingResumeRef = useRef<number | null>(null);
  const lastSentLocationRef = useRef(0);
  // ReturnType instead of `number`: under DOM typings setInterval returns a
  // number, but consumers compiled with Node typings see NodeJS.Timeout.
  const setLocationTimerRef = useRef<ReturnType<
    typeof globalThis.setInterval
  > | null>(null);
  const playerRef = useRef<VideoJsLikePlayer | null>(null);
  const attachedPlayerRef = useRef<VideoJsLikePlayer | null>(null);
  const maxScoreRef = useRef(0);
  // Ref so the latest callback fires without re-binding player handlers.
  const onCompletedRef = useRef(onCompleted);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  // Watched-coverage state: closed [start, end] segments of *naturally played*
  // video plus the currently-growing segment. Completion and score are
  // computed from this coverage — never from the playhead position — so
  // seeking (or being dropped) at the end can not complete the lesson.
  const segmentsRef = useRef<WatchedSegment[]>([]);
  const openSegmentStartRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  // Furthest position reached through natural playback; the forward-seek lock
  // clamps to it and resume restores it from suspend_data.
  const maxWatchedRef = useRef(0);

  const post = useCallback(
    (message: ScormPlayerToShim) => {
      if (!enabled) return;
      const origins = parentOriginsRef.current.length
        ? parentOriginsRef.current
        : ['*'];
      try {
        origins.forEach((origin) => globalThis.parent.postMessage(message, origin));
      } catch {
        // postMessage cannot fail across origins, but defensively swallow.
      }
    },
    [enabled],
  );

  const closeOpenSegment = useCallback((endSeconds: number) => {
    const start = openSegmentStartRef.current;
    openSegmentStartRef.current = null;
    if (start === null || endSeconds <= start) return;
    segmentsRef.current.push([start, endSeconds]);
    if (segmentsRef.current.length > 50) {
      segmentsRef.current = mergeSegments(segmentsRef.current);
    }
  }, []);

  const allSegments = useCallback((): WatchedSegment[] => {
    const start = openSegmentStartRef.current;
    if (start !== null && lastTimeRef.current > start) {
      return [...segmentsRef.current, [start, lastTimeRef.current]];
    }
    return segmentsRef.current;
  }, []);

  const sendLocation = useCallback(
    (seconds: number, force = false) => {
      if (!Number.isFinite(seconds)) return;
      // Never report a resume point ahead of what was actually watched — a
      // blocked forward seek can briefly land the playhead past the lock
      // before the snap-back corrects it. Once completed, seeking is free
      // again, so the real position is the right resume point.
      const location =
        disableSeek && !completedRef.current
          ? Math.min(
              seconds,
              maxWatchedRef.current + FORWARD_SEEK_TOLERANCE_SECONDS,
            )
          : seconds;
      if (!force && Math.abs(location - lastSentLocationRef.current) < 1) return;
      lastSentLocationRef.current = location;

      const segments = allSegments();
      let score: number | undefined;
      const player = playerRef.current;
      try {
        const duration =
          player && !player.isDisposed?.() ? player.duration() : 0;
        if (duration > 0) {
          // Watched-coverage percentage. Monotonic by construction (coverage
          // only grows), but clamp through maxScoreRef anyway so a transient
          // mis-read never lowers the reported score.
          const pct = Math.min(
            100,
            Math.round((coverageSeconds(segments) / duration) * 100),
          );
          if (pct > maxScoreRef.current) maxScoreRef.current = pct;
          score = maxScoreRef.current;
        }
      } catch {
        // Player torn down mid-read; send the location without a score.
      }

      post({
        kind: SCORM_MESSAGE_KINDS.SET_LOCATION,
        lessonLocation: location,
        score,
        suspendData: serializeSuspendData({
          t: location,
          max: maxWatchedRef.current,
          seg: segments,
        }),
      });
    },
    [post, disableSeek, allSegments],
  );

  const checkCompletion = useCallback(
    (player: VideoJsLikePlayer, currentSeconds: number) => {
      if (completedRef.current) return;
      let duration = 0;
      try {
        duration = player.duration();
      } catch {
        return;
      }
      if (duration <= 0) return;
      const required = Math.min(
        duration * completionThreshold,
        Math.max(duration - END_TOLERANCE_SECONDS, 0),
      );
      if (coverageSeconds(allSegments()) < required) return;
      completedRef.current = true;
      sendLocation(currentSeconds, true);
      post({ kind: SCORM_MESSAGE_KINDS.COMPLETE });
      // Completion lifts all seek restrictions — let the host restore the UI.
      onCompletedRef.current?.();
    },
    [allSegments, completionThreshold, post, sendLocation],
  );

  /** Persist the freshest position + coverage and ask the shim to commit now. */
  const flushProgress = useCallback(() => {
    const player = playerRef.current;
    try {
      if (player && !player.isDisposed?.()) {
        closeOpenSegment(lastTimeRef.current);
        sendLocation(player.currentTime() ?? lastTimeRef.current, true);
      }
    } catch {
      // Player torn down; flush whatever the shim already holds.
    }
    post({ kind: SCORM_MESSAGE_KINDS.FLUSH });
  }, [closeOpenSegment, sendLocation, post]);

  useEffect(() => {
    if (!enabled) return;
    parentOriginsRef.current = discoverParentOrigins();

    const onMessage = (event: MessageEvent) => {
      if (
        parentOriginsRef.current.length > 0 &&
        !parentOriginsRef.current.includes(event.origin)
      ) {
        return;
      }
      const data = event.data as ScormResumeResponseMessage | undefined;
      if (!data || typeof data !== 'object') return;
      if (data.kind !== SCORM_MESSAGE_KINDS.RESUME_RESPONSE) return;

      // The LMS already reports this lesson completed (a relaunch) — no
      // further coverage is required and seek restrictions no longer apply.
      if (data.completed && !completedRef.current) {
        completedRef.current = true;
        onCompletedRef.current?.();
      }

      const location =
        typeof data.lessonLocation === 'number' ? data.lessonLocation : 0;
      const stored = parseSuspendData(data.suspendData);
      if (stored) {
        segmentsRef.current = mergeSegments([
          ...segmentsRef.current,
          ...stored.seg,
        ]);
        if (stored.max > maxWatchedRef.current) {
          maxWatchedRef.current = stored.max;
        }
      }
      // Packages exported before suspend_data support only stored
      // lesson_location; it was only reachable by watching, so trust it as
      // watched. This also MUST happen before the resume seek below —
      // otherwise the forward-seek lock would snap the resume back to 0.
      if (location > maxWatchedRef.current) maxWatchedRef.current = location;

      const resumeTo = stored && stored.t > 0 ? stored.t : location;
      if (resumeTo <= 0) return;
      const player = playerRef.current;
      let seeked = false;
      if (player) {
        try {
          if (player.duration() > 0) {
            (player as unknown as PlayerWithSeek).currentTime(resumeTo);
            lastTimeRef.current = resumeTo;
            seeked = true;
          }
        } catch {
          // Metadata not ready yet; fall through to the pending resume.
        }
      }
      if (!seeked) pendingResumeRef.current = resumeTo;
    };

    globalThis.addEventListener('message', onMessage);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushProgress();
    };
    globalThis.addEventListener('pagehide', flushProgress);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // The heartbeat interval is owned entirely by this effect (created AND
    // cleared here) so a re-run can never strand the tracking without one —
    // setupPlayerTracking early-returns for an already-attached player and
    // would not recreate it.
    if (setLocationTimerRef.current === null) {
      setLocationTimerRef.current = globalThis.setInterval(() => {
        // Read the *current* player (a remount may have swapped it) and skip
        // if it has been disposed — video.js nulls its internals on dispose,
        // so currentTime() would throw "Cannot read properties of null".
        const activePlayer = playerRef.current;
        if (!activePlayer || activePlayer.isDisposed?.()) return;
        try {
          sendLocation(activePlayer.currentTime());
        } catch {
          // Player torn down between the disposed check and the read; ignore.
        }
      }, SET_LOCATION_INTERVAL_MS);
    }

    return () => {
      globalThis.removeEventListener('message', onMessage);
      globalThis.removeEventListener('pagehide', flushProgress);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (setLocationTimerRef.current !== null) {
        globalThis.clearInterval(setLocationTimerRef.current);
        setLocationTimerRef.current = null;
      }
    };
  }, [enabled, flushProgress, sendLocation]);

  const setupPlayerTracking = useCallback<SetupPlayerTracking>(
    (player) => {
      if (!player || (!enabled && !disableSeek)) return;
      if (attachedPlayerRef.current === player) return;

      // A player remount (e.g. a layout toggle recreates the <video>) resets
      // the playhead to 0 — restore the last known position instead of
      // silently restarting the lesson.
      if (attachedPlayerRef.current !== null && lastTimeRef.current > 1) {
        closeOpenSegment(lastTimeRef.current);
        pendingResumeRef.current = lastTimeRef.current;
        lastTimeRef.current = 0;
      }
      attachedPlayerRef.current = player;
      playerRef.current = player;

      if (enabled && !initSentRef.current) {
        initSentRef.current = true;
        post({ kind: SCORM_MESSAGE_KINDS.INIT });
      }

      if (disableSeek) {
        // Allow rewatching (seek backward) but block any forward jump past
        // the furthest naturally-watched point. Snap back on BOTH `seeking`
        // and `seeked`: a currentTime write during `seeking` is sometimes
        // ignored mid-seek, while `seeked` reliably lands. The guard stops
        // the corrective seek from looping.
        const blockForwardSeek = () => {
          // Completed lessons are freely seekable — the learner already
          // watched everything the threshold required.
          if (completedRef.current) return;
          try {
            const target = player.currentTime() ?? 0;
            if (target > maxWatchedRef.current + FORWARD_SEEK_TOLERANCE_SECONDS) {
              (player as unknown as PlayerWithSeek).currentTime(
                maxWatchedRef.current,
              );
            }
          } catch {
            // Player torn down mid-seek; nothing to clamp.
          }
        };
        player.on('seeking', blockForwardSeek);
        player.on('seeked', blockForwardSeek);
      }

      const onLoadedMetadata = () => {
        const resume = pendingResumeRef.current;
        if (resume !== null && resume > 0) {
          try {
            (player as unknown as PlayerWithSeek).currentTime(resume);
            lastTimeRef.current = resume;
          } catch {
            // Some players reject seeking before the first frame is ready; skipped if so.
          }
          pendingResumeRef.current = null;
        }
      };

      const onTimeUpdate = () => {
        let current = 0;
        try {
          current = player.currentTime() ?? 0;
        } catch {
          return;
        }
        const previous = lastTimeRef.current;
        lastTimeRef.current = current;
        const delta = current - previous;
        const isNaturalPlayback =
          delta > 0 &&
          delta < NATURAL_DELTA_MAX_SECONDS &&
          !(player.seeking?.() ?? false);
        if (isNaturalPlayback) {
          if (openSegmentStartRef.current === null) {
            openSegmentStartRef.current = previous;
          }
          if (current > maxWatchedRef.current) maxWatchedRef.current = current;
        } else if (delta !== 0) {
          // A jump (seek) — close the watched segment at its last natural position.
          closeOpenSegment(previous);
        }
        sendLocation(current);
        checkCompletion(player, current);
      };

      const onPause = () => {
        closeOpenSegment(lastTimeRef.current);
        try {
          sendLocation(player.currentTime() ?? lastTimeRef.current, true);
        } catch {
          // Player torn down; the next flush will carry the state.
        }
        post({ kind: SCORM_MESSAGE_KINDS.FLUSH });
      };

      const onEnded = () => {
        closeOpenSegment(lastTimeRef.current);
        let current = lastTimeRef.current;
        try {
          current = player.currentTime() ?? current;
        } catch {
          // Keep the last known position.
        }
        sendLocation(current, true);
        // No unconditional COMPLETE here: `ended` is reachable by seeking to
        // the end, so completion still requires real watched coverage.
        checkCompletion(player, current);
        post({ kind: SCORM_MESSAGE_KINDS.FLUSH });
      };

      player.on('loadedmetadata', onLoadedMetadata);
      player.on('timeupdate', onTimeUpdate);
      player.on('pause', onPause);
      player.on('ended', onEnded);
    },
    [
      enabled,
      disableSeek,
      post,
      sendLocation,
      checkCompletion,
      closeOpenSegment,
    ],
  );

  return { setupPlayerTracking };
}
